import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { EmailTemplate } from "types/emailTemplateTypes";
import { parseEmailTemplateResponse } from "utils/apiParser/emailTemplateParser";

const apiUrl = process.env.REACT_APP_BACKEND_URL;

interface EmailTemplatesState {
    templates: EmailTemplate[];
    selectedTemplate: EmailTemplate | null;
    fetchEmailTemplatesLoading: boolean;
    error: string | null;
}

const initialState: EmailTemplatesState = {
    templates: [],
    selectedTemplate: null,
    fetchEmailTemplatesLoading: false,
    error: null,
}

export const fetchEmailTemplates = createAsyncThunk(
    'emailTemplates/fetch',
    async ({ orgId }: { orgId: number }, { rejectWithValue }) => {
        try {
            const res = await axios.get(`${apiUrl}/orgs/${orgId}/emailTemplates`, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
            return res.data.data.map(parseEmailTemplateResponse);

        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to fetch email templates');
        }
    }
)

export const createEmailTemplate = createAsyncThunk(
    'emailTemplates/create',
    async ({ orgId, template }: { orgId: number; template: any }, { rejectWithValue }) => {
        try {
            const res = await axios.post(`${apiUrl}/orgs/${orgId}/emailTemplates`, template, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
            return parseEmailTemplateResponse(res.data[0]);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to create template');
        }
    }
)

export const updateEmailTemplate = createAsyncThunk(
    'emailTemplates/update',
    async ({ orgId, templateId, template }: { orgId: number; templateId: number; template: any }, { rejectWithValue }) => {
        try {
            const res = await axios.patch(`${apiUrl}/orgs/${orgId}/emailTemplates/${templateId}`, template, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
            return parseEmailTemplateResponse(res.data);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to update template');
        }
    }
)


export const deleteEmailTemplate = createAsyncThunk(
    'emailTemplates/delete',
    async ({ orgId, templateId }: { orgId: number; templateId: number; }, { rejectWithValue }) => {
        try {
            await axios.delete(`${apiUrl}/orgs/${orgId}/emailTemplates/${templateId}`, {
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });
            return templateId;
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to delete template');
        }
    }
)

const emailTemplatesSlice = createSlice({
    name: 'emailTemplates',
    initialState,
    reducers: {
        clearSelectedTemplate(state) {
            state.selectedTemplate = null;
        },
        setSelectedTemplate(state, action) {
            state.selectedTemplate = action.payload;
        },
        clearEmailTemplateState () {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchEmailTemplates.pending, (state) => {
                state.fetchEmailTemplatesLoading = true;
                state.error = null;
            })
            .addCase(fetchEmailTemplates.fulfilled, (state, action) => {
                state.templates = action.payload;
                state.fetchEmailTemplatesLoading = false;
            })
            .addCase(fetchEmailTemplates.rejected, (state, action) => {
                state.error = action.payload as string;
                state.fetchEmailTemplatesLoading = false;
            })
            .addCase(createEmailTemplate.fulfilled, (state, action) => {
                state.templates.push(action.payload);
            })
            .addCase(updateEmailTemplate.fulfilled, (state, action) => {
                const index = state.templates.findIndex((t) => t.id === action.payload.id);
                if (index !== -1) {
                    state.templates[index] = action.payload;
                }
            })
            .addCase(deleteEmailTemplate.fulfilled, (state, action) => {
                state.templates = state.templates.filter(t => t.id !== action.payload)
            })
    },
});

export const { clearSelectedTemplate, setSelectedTemplate, clearEmailTemplateState } = emailTemplatesSlice.actions;
export default emailTemplatesSlice.reducer;